import type { HeadToHead as HeadToHeadData, ModelCard as ModelCardData, ModelHistoryEntry } from '../../api'
import { Detail } from '../Detail'
import { date, dateTime } from './format'
import { Fact, Metric, PanelHeading } from './parts'

/**
 * Which model is answering, which have answered before, and what each one scored.
 *
 * **Provenance, on its own tab.** These three sections used to sit below the record and the
 * reliability chart, on a page that ran to nine thousand pixels — so the reader who wanted to know
 * how good the model is scrolled past the model's biography, and the reader who wanted the
 * biography scrolled past the record. They are different questions asked by different people, and
 * the only thing they shared was a file.
 */
export function VersionsPanel({
  card,
  headToHead,
  models,
}: {
  card: ModelCardData
  headToHead: HeadToHeadData | null
  models: ModelHistoryEntry[]
}) {
  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-line bg-surface p-4">
        <h3 className="text-sm font-medium">The model answering right now</h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Version" value={<code className="text-xs">{card.modelVersion}</code>} />
          <Row label="Features" value={`${card.featureCount}`} />
          <Row label="Trained through" value={dateTime(card.trainedThrough)} />
          <Row label="Rated players" value={card.ratedPlayers.toLocaleString()} />
          <Row label="Calibration" value={<code className="text-xs">{card.calibration.kind}</code>} />
          <Row label="Ratings as of" value={dateTime(card.ratingsAsOf)} />
        </dl>
        {card.modelVersion.endsWith('-dirty') ? (
          <p className="mt-3 border-t border-line pt-3 text-xs text-warn">
            The <code>-dirty</code> suffix means this model was exported from a working tree with
            uncommitted changes, so the exact training code is not recoverable from git. Recorded
            rather than hidden.
          </p>
        ) : null}
      </section>

      {headToHead ? <HeadToHead data={headToHead} /> : null}

      {models.length ? <ModelHistory models={models} /> : null}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line/50 pb-1">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}

/**
 * Two live models, measured on the matches they both scored.
 *
 * **Why this is not just their two records side by side.** A model may only be judged on matches
 * played after its training cut-off, so a newly retrained one is eligible for a handful of recent
 * matches while the model it would replace is eligible for weeks of them. Reading those two
 * percentages against each other compares different opponents on different days in different
 * numbers, and most of the gap is the sample rather than the model.
 *
 * Restricting both to the matches they have in common holds the sample fixed. The basis is stated
 * plainly because early on it is small, and a five-match lead is not evidence of anything.
 */
function HeadToHead({ data }: { data: HeadToHeadData }) {
  const thin = data.basis < 30

  return (
    <section>
      <PanelHeading>Live model against its challenger</PanelHeading>
      {/*
        Trimmed, because it used to explain what a shadow model is — which the Shadow badge in the
        history below now says on tap — and then the history repeated both models a screen later.
        What survives is the part this section alone provides: the SAME-SAMPLE comparison. Each
        model's own record covers different matches, because each is barred from the ones it trained
        on, so reading those two percentages against each other compares different opponents on
        different days in different numbers.
      */}
      <p className="mt-1 max-w-3xl text-sm text-ink-soft">
        Measured on the {data.basis} prediction{data.basis === 1 ? '' : 's'} they have in common, so
        the only thing differing between them is the model.
        {thin
          ? ' Too few so far to separate the two — a lead over this many matches is well inside what chance produces, and it is here to be watched rather than acted on.'
          : ''}
      </p>

      <ul className="mt-3 grid gap-3 sm:grid-cols-2">
        {data.models.map((model) => (
          <li key={model.version} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="tabular text-sm font-semibold text-ink">{model.version}</span>
              <span className="text-xs uppercase tracking-wide text-ink-faint">
                {model.role === 'champion' ? 'live' : 'shadow'}
              </span>
            </div>
            {model.stats ? (
              <dl className="mt-2 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <Fact
                  label="Accuracy"
                  value={`${(model.stats.accuracy * 100).toFixed(1)}%`}
                />
                <Fact label="Brier" value={model.stats.brier.toFixed(4)} />
                <Fact label="Log loss" value={model.stats.logLoss.toFixed(3)} />
                <Fact label="Scored" value={`${model.stats.count}`} />
              </dl>
            ) : (
              <p className="mt-2 text-sm text-ink-soft">Nothing scored yet.</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Every model that has served this site, and what each actually scored.
 *
 * **The rule this table exists to make visible: a model is never judged on a match it was trained
 * on.** That sounds obvious and was not enforced — the first shipped model claimed a training cutoff
 * before the tournament while its real cutoff sat eight hours into day one, so 108 fits to its own
 * training data were published as an 83% prediction record. Each row therefore reports the record on
 * matches the model is *allowed* to be judged on, and states separately how many of its predictions
 * were excluded as in-sample.
 *
 * Laid out as stacked cards rather than a table on purpose: a wide table of metrics is the fastest
 * way to reintroduce the horizontal scrolling that this site was just fixed for on a phone.
 */
function ModelHistory({ models }: { models: ModelHistoryEntry[] }) {
  /*
   * Live first, then its shadow, then everything retired, newest first.
   *
   * Not server order. The two models that are running right now are the two a reader is asking
   * about, and leaving them wherever the query happened to put them buried the live one under
   * retired versions. Role decides the top of the list; the clock only breaks ties below it.
   */
  const rank = (model: ModelHistoryEntry) =>
    model.role === 'champion' ? 0 : model.role === 'shadow' ? 1 : 2

  const served = models
    .filter((model) => model.activatedAt !== null)
    .sort((a, b) => rank(a) - rank(b) || (b.activatedAt ?? 0) - (a.activatedAt ?? 0))

  const neverServed = models.filter((model) => model.activatedAt === null)

  return (
    <section>
      <PanelHeading
        lead="Each version is scored only on matches played after its training cut-off. A model that
          has seen a match already knows the answer, so counting that would measure memory rather
          than skill."
      >
        Model history
      </PanelHeading>

      <ol className="mt-3 space-y-3">
        {served.map((model) => (
          <ModelCard key={model.version} model={model} />
        ))}
      </ol>

      {/*
        Never-served exports, out of the way.
        
        Two of these are lab artefacts titled with a git sha — `6106e66`, `f532157-dirty` — that were
        never deployed, have no record, and carry the line "No matches yet that this version is
        allowed to be judged on". Publishing them is right; leading with them is not. They pushed the
        two models that actually serve the site down the page and made a public page read as a debug
        view.
      */}
      {neverServed.length > 0 ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-ink-faint hover:text-ink-soft">
            {neverServed.length} earlier {neverServed.length === 1 ? 'export' : 'exports'} that never
            served the site
          </summary>
          <ol className="mt-2 space-y-3">
            {neverServed.map((model) => (
              <ModelCard key={model.version} model={model} />
            ))}
          </ol>
        </details>
      ) : null}
    </section>
  )
}

function ModelCard({ model }: { model: ModelHistoryEntry }) {
  return (
    <li className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="tabular text-sm font-semibold text-ink">{model.version}</span>
        {model.name ? <span className="text-sm text-ink-soft">{model.name}</span> : null}
        {model.role === 'champion' ? (
          <span className="rounded bg-accent-bg px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-accent">
            Live
          </span>
        ) : null}
        {model.role === 'shadow' ? (
          <Detail
            label="What a shadow model is"
            heading={
              <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-ink-soft">
                Shadow
              </span>
            }
          >
            Scores every match alongside the live model, but publishes nothing. It is the only
            way to tell whether a retrain actually helped: both see the same matches.
          </Detail>
        ) : null}
      </div>

      {model.byline ? (
        <p className="mt-1 text-sm text-ink-soft">{model.byline}</p>
      ) : null}

      <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <Fact label="Trained through" value={date(model.trainedThrough)} />
        <Fact
          label="Served"
          value={
            model.activatedAt
              ? `${date(model.activatedAt)} — ${model.retiredAt ? date(model.retiredAt) : 'now'}`
              : 'never deployed'
          }
        />
        <Fact label="Features" value={String(model.featureCount)} />
        {model.provenance ? <Fact label="Built from" value={model.provenance} /> : null}
      </dl>

      <div className="mt-3 border-t border-line pt-3 text-xs">
        {model.stats ? (
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <Metric label="Scored" value={`${model.stats.count}`} />
            <Metric label="Correct" value={`${(model.stats.accuracy * 100).toFixed(0)}%`} />
            <Metric label="Brier" value={model.stats.brier.toFixed(4)} />
            <Metric label="Log loss" value={model.stats.logLoss.toFixed(4)} />
          </div>
        ) : (
          <p className="text-ink-faint">
            No matches yet that this version is allowed to be judged on.
          </p>
        )}

        {model.excludedInSample > 0 ? (
          <p className="mt-2 text-ink-faint">
            {model.excludedInSample} prediction{model.excludedInSample === 1 ? '' : 's'}{' '}
            excluded: the matches were in this model&rsquo;s training data, so they measure
            recall rather than foresight.
          </p>
        ) : null}
      </div>
    </li>
  )
}

